<script setup>
import { ref, watch, computed, defineProps } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import Previewer from '@/components/Previewer.vue';
import { CloudUploadIcon } from 'vue-tabler-icons';

defineProps({ draft: { type: Object, required: true } });

const canvas = ref();
const store = useStore();
const route = useRoute();

const errorMessage = ref('');
const progressStatus = ref('');
const inProgress = ref(false);
const previewerOperations = computed(() => store.getters['ci/previewerOperations'](route.params.ciId));

async function save() {
  errorMessage.value = '';
  progressStatus.value = '';
  inProgress.value = true;
  try {
    await store.dispatch('ci/updateCIDraft', {
      ciId: route.params.ciId,
    });
    progressStatus.value = 'Default Setting Saved Successfully!';
  } catch (err) {
    errorMessage.value = err.message;
  } finally {
    inProgress.value = false;
    setTimeout(() => {
      progressStatus.value = '';
      errorMessage.value = '';
    }, 5000);
  }
}

const previewerContainer = ref();
const previewerRef = ref();

watch(previewerContainer, (el) => {
  el.appendChild(previewerRef.value.$el);
});
</script>

<template>
  <section class="ci-draft">
    <header class="col-span-3 flex flex-wrap gap-x-5">
      <h2 class="col-span-3 mx-6 flex flex-col py-2">
        <small class="-mb-2 text-xs text-brand-700"> {{ draft.namespace }} </small>
        <span class="text-xl">{{ draft.name }} </span>
      </h2>
      <nav class="flex flex-grow gap-4 pb-0">
        <router-link
          v-slot="{ href, navigate, isActive }"
          custom
          :to="`/configurable-items/${draft.namespace}:${draft.name}/draft/config`"
        >
          <a :href="href" :class="{ 'text-brand-500': isActive, 'text-gray-600': !isActive }" @click="navigate">
            Configuration Setup
          </a>
        </router-link>
        <router-link
          v-slot="{ href, navigate, isActive }"
          custom
          :to="`/configurable-items/${draft.namespace}:${draft.name}/draft/environment/camera`"
        >
          <a :href="href" :class="{ 'text-brand-500': isActive, 'text-gray-600': !isActive }" @click="navigate">
            Environment | Rendering
          </a>
        </router-link>
      </nav>
      <div v-if="errorMessage || progressStatus" class="mr-4 flex flex-grow p-1">
        <div
          class="text-teal inline-flex items-center rounded-full bg-white p-2 text-sm leading-none shadow"
          :class="{ 'text-red-500': errorMessage, 'text-green-500': progressStatus }"
        >
          <span
            class="inline-flex h-6 items-center justify-center rounded-full px-3 text-white"
            :class="{ 'bg-red-500': errorMessage, 'bg-blue-500': progressStatus }"
            >{{ errorMessage ? 'Error' : 'Success' }}
          </span>
          <span class="inline-flex px-2">{{ errorMessage ? errorMessage : progressStatus }}</span>
        </div>
      </div>
      <div v-show="canvas" class="flex items-end">
        <button class="ci-draft__cloud-save" :disabled="inProgress || !previewerOperations" @click="save">
          <cloud-upload-icon /> Save
        </button>
      </div>
      <div class="w-full bg-white">
        <router-view name="top"></router-view>
      </div>
    </header>
    <main class="sticky top-4 bg-white" style="height: calc(100% - 4.3rem)">
      <router-view v-slot="{ Component }" :canvas="canvas">
        <component :is="Component">
          <div ref="previewerContainer" />
        </component>
      </router-view>
      <previewer
        :id="`${draft.namespace}-${draft.name}-previewer`"
        :key="`${draft.namespace}-${draft.name}-previewer`"
        ref="previewerRef"
        v-model:canvas="canvas"
        :draft="draft"
      />
    </main>
  </section>
</template>

<style lang="postcss" scoped>
.ci-draft {
  @apply flex h-full flex-col;
}

nav a {
  @apply flex items-center border-t px-6 py-2 text-xs font-semibold uppercase tracking-wider;
}
nav a:hover {
  @apply bg-gray-200 text-gray-900;
}

.ci-draft__cloud-save {
  @apply mx-3 flex items-center gap-2 self-center rounded bg-brand-500 px-4 py-1 tracking-wider text-white;
}
.ci-draft__cloud-save:hover {
  @apply bg-brand-600 shadow-lg;
}
.ci-draft__cloud-save:disabled {
  @apply cursor-not-allowed opacity-25;
}
</style>
